/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */

import { AxiosError } from 'axios';

interface TableColumn<T> {
  title: string // Header text for the column
  column: keyof T // Key to access data in each item of the data array
}

interface Category {
  name: string
  childrens: (string | Category)[]
  subOptions?: { [key: string]: string[] } // Define sub-options for dynamic childrens like 'Role'
}

/**
 * Extract error message from API error response
 * @param error - The error object from catch block
 * @param defaultMessage - Default message if no specific message is found
 * @returns The error message string
 */
export function getErrorMessage(error: unknown, defaultMessage = 'An error occurred'): string {
  if (error instanceof AxiosError) {
    // Check for message in response data
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    // Check for error in response data
    if (error.response?.data?.error) {
      return error.response.data.error;
    }
    // Check for status text
    if (error.response?.statusText) {
      return error.response.statusText;
    }
    // Network error
    if (error.message) {
      return error.message;
    }
  }
  // Generic error with message
  if (error instanceof Error) {
    return error.message;
  }
  return defaultMessage;
}

export function addStarAfterFourChars(str: string) {
  const firstFour = str.substring(0, 6)
  const stars = '********'
  return firstFour + stars
}

export const IncidentsHeadersGeneric: TableColumn<{
  Report_No: string
  Status: string
  Severity_Level: string
  Name_of_Candidate: string
  Name_of_Employer: string
  Date_Reported: string
  Updated_At: string
  Created_By: string
  Updated_By: string
}>[] = [
  { title: 'Report_No', column: 'Report_No' },
  { title: 'Status', column: 'Status' },
  { title: 'Severity_Level', column: 'Severity_Level' },
  { title: 'Name_of_Candidate', column: 'Name_of_Candidate' },
  { title: 'Name_of_Employer', column: 'Name_of_Employer' },
  { title: 'Date_Reported', column: 'Date_Reported' },
  { title: 'Updated_At', column: 'Updated_At' },
  { title: 'Created_By', column: 'Created_By' },
  { title: 'Updated_By', column: 'Updated_By' },
]

export const columns: TableColumn<{ name: string; status: string }>[] = [
  { title: 'name', column: 'name' },
  { title: 'status', column: 'status' },
]

export const Data = [
  {
    name: 'hunesh',
    status: 'Open',
  },
]

export function formatDate(dateString: string): string {
  if (dateString == null || dateString == undefined || dateString == '') {
    return 'Not Specified'
  } else {
    const date = new Date(dateString)
    const options: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }
    const formattedDate = date.toLocaleDateString('en-US', options)
    return formattedDate
  }
}

export const statusBgColor = (status: string | undefined): string => {
  switch (status) {
    case 'Open':
      return 'lightGreen'
    case 'open':
      return 'lightGreen'
    case 'close':
      return 'gray3'
    case 'closed':
      return 'gray3'
    case 'Lapsed':
      return 'paleYellow'
    case 'Not Active':
      return 'palePink'
    case 'active':
      return 'lightGreen'
    case 'archived':
      return 'paleYellow'
    case 'suspended':
      return 'palePink'

    default:
      return 'gray3'
  }
}

export const statusBgColorWhistleblowing = (
  status: string | undefined
): string => {
  switch (status) {
    case 'closed':
      return 'lightGreen'
    case 'close':
      return 'lightGreen'
    case 'open':
      return 'palePink'
    case 'Open':
      return 'palePink'
    case 'Lapsed':
      return 'paleYellow'
    case 'Not Active':
      return 'palePink'
    case 'active':
      return 'lightGreen'
    case 'archived':
      return 'paleYellow'
    case 'suspended':
      return 'palePink'

    default:
      return 'gray3'
  }
}

export function formatDateToUsDate(dateString: string): string {
  // Create a Date object from the input string

  if (dateString == null) {
    return ''
  }
  const date = new Date(dateString)
  // Extract the components of the date
  const year = date.getFullYear()
  // Months are zero-based, so add 1 to get the correct month
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  // Format the date as MM/DD/YYYY
  return `${year}-${month}-${day}`
}

export const calculatePageCount = (dataLength: number): number => {
  if (dataLength < 8) {
    return 1
  } else {
    return Math.ceil(dataLength / 7)
  }
}

// Single source of truth for the completion -> colour mapping used by every
// scorecard (both the circular progress and the per-field bars). Green means
// truly complete (100%), orange means in progress, red means nothing filled.
export const COMPLETION_COLORS = {
  complete: '#01B38B', // green
  inProgress: '#FFA800', // orange
  empty: '#FF6666', // red
} as const

export function completionColor(percentage: number): string {
  if (percentage >= 100) return COMPLETION_COLORS.complete
  if (percentage > 0) return COMPLETION_COLORS.inProgress
  return COMPLETION_COLORS.empty
}

// A value counts as "filled" only when it is not null, not undefined and not an
// empty string. This is the single definition of "empty" for all scorecards.
export function isFieldFilled(value: unknown): boolean {
  return value !== null && value !== undefined && value !== ''
}

export function calculateNonNullPercentage(
  items: (string | null | undefined)[]
): {
  percentage: number
  color: string
} {
  const totalItems: number = items.length
  const filledItems: number = items.filter(isFieldFilled).length

  if (totalItems === 0) {
    return { percentage: 0, color: completionColor(0) }
  }

  let percentage = Math.round((filledItems / totalItems) * 100)
  // Never report 100% unless every field is genuinely filled. Guards rounding
  // cases such as 199/200 = 99.5 which would otherwise round up to 100.
  if (percentage === 100 && filledItems < totalItems) percentage = 99

  return { percentage, color: completionColor(percentage) }
}

// Single, shared definition of which fields count towards a record's completion.
// Used by BOTH the overall circle and the per-category bars on every SCR page
// (candidate self-view and admin/lead views) and by the staff lists, so the same
// record always scores identically everywhere. Behaviour B: verificationDate +
// verifiedBy count, so 100% means "documents provided AND verified".

// The forms hide Duration and the Risk Assessment Document when the risk
// assessment status is "No", so those fields only count when it is not "No".
// Training stores 'NO', safeguarding stores 'no'.
const requiresRiskFollowUp = (status: unknown): boolean =>
  String(status ?? '').toLowerCase() !== 'no'

export const getSafeguardingScoreFields = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  category: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  record: any
): (string | null | undefined)[] => {
  const fields = [
    record?.document,
    record?.documentNo,
    record?.documentType,
    record?.verificationDate,
    record?.verifiedBy,
  ]
  if (category?.riskAssessment) {
    fields.push(record?.riskAssessmentStatus)
    if (requiresRiskFollowUp(record?.riskAssessmentStatus)) {
      fields.push(record?.duration, record?.riskDocument)
    }
  }
  if (category?.issueDate) fields.push(record?.issueDate)
  if (category?.expiryDate) fields.push(record?.expiryDate)
  return fields
}

export const getTrainingScoreFields = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  record: any
): (string | null | undefined)[] => {
  const fields = [
    record?.certificateNumber,
    record?.date,
    record?.reviewDate,
    record?.verifiedBy,
    record?.verificationDate,
    record?.certificate,
    record?.riskAssessmentStatus,
  ]
  if (requiresRiskFollowUp(record?.riskAssessmentStatus)) {
    fields.push(record?.duration, record?.riskDocument)
  }
  return fields
}

// Records are matched to their category by id, never by array position: the
// staff-list API returns records unordered and can include records whose
// category has since been deleted. If a category somehow has several records,
// the last one wins, matching what the record pages display.
const findCategoryRecord = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  records: any[] | undefined,
  categoryId: string,
  categoryKey: 'safeguardingRecordCategoryId' | 'trainingRecordCategoryId'
) => {
  const matches = (records || []).filter(
    (record) => record?.[categoryKey] === categoryId
  )
  return matches[matches.length - 1]
}

// Aggregate every category's fields into a single overall score (the circle on
// the record pages and the bars on the staff lists).
export const aggregateSafeguardingScore = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  categories: any[] | undefined,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  records: any[] | undefined
) =>
  calculateNonNullPercentage(
    (categories || []).flatMap((category) =>
      getSafeguardingScoreFields(
        category,
        findCategoryRecord(records, category?.id, 'safeguardingRecordCategoryId')
      )
    )
  )

export const aggregateTrainingScore = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  categories: any[] | undefined,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  records: any[] | undefined
) =>
  calculateNonNullPercentage(
    (categories || []).flatMap((category) =>
      getTrainingScoreFields(
        findCategoryRecord(records, category?.id, 'trainingRecordCategoryId')
      )
    )
  )

export function calculateCircularPercentage(numbers: number[]): {
  percentage: number
  color: string
} {
  const totalNumbers: number = numbers.length
  const nonZeroNumbers: number = numbers.filter((num) => num !== 0).length
  let percentage: number

  if (totalNumbers === 0) {
    percentage = 0
  } else {
    percentage = Math.round((nonZeroNumbers / totalNumbers) * 100)
  }

  return { percentage, color: completionColor(percentage) }
}

export function calculateTraningPercentageAndColor(
  total: number,
  givenNumber: number
): { percentage: number; color: string } {
  let percentage: number

  if (total === 0) {
    percentage = 0
  } else {
    percentage = Math.round((givenNumber / total) * 100)
    percentage = Math.min(percentage, 100) // Ensure percentage doesn't exceed 100
  }

  return { percentage, color: completionColor(percentage) }
}

export function convertBytesToKB(sizeInBytes: number) {
  return Math.round(sizeInBytes / 1024)
}

export function removeNullEntries<T>(obj: T): Partial<T> {
  const newObj: Partial<T> = {}

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key as keyof T]

      if (
        value !== null &&
        typeof value === 'object' &&
        !Array.isArray(value)
      ) {
        // Recursively remove null entries for nested objects
        newObj[key as keyof T] = removeNullEntries(value) as T[keyof T]
      } else if (value !== null) {
        newObj[key as keyof T] = value
      }
    }
  }

  return newObj
}
export const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

// export const FilterCategories: Category[] = [
//   {
//     name: 'Profile & Employment',
//     childrens: ['All', '360 Role', 'Work Status'],
//     subOptions: {
//       '360 Role': [''], // Dynamic options for 'Role'
//       'Work Status': ['All', 'Full-time', 'Part-time', 'etc'], // Dynamic options for 'Role'
//     },
//   },
//   {
//     name: 'Safeguarding',
//     childrens: [''],
//   },
//   {
//     name: 'Training',
//     childrens: [''],
//   },
//   {
//     name: 'Policy',
//     childrens: ['', 'Role', 'Status'],
//   },
// ]

export const FilterCategories: Category[] = [
  {
    name: 'Profile & Employment',
    childrens: [
      {
        name: '360 Role',
        childrens: [''],
      },
      {
        name: 'Work Status',
        childrens: [''],
      },
    ],
  },
  {
    name: 'Training',
    childrens: [''],
  },
  {
    name: 'Safeguarding',
    childrens: [''],
  },
  {
    name: 'Policies',
    childrens: [''],
  },
]
